/* eslint-disable @typescript-eslint/member-ordering */
import { ID } from '@datorama/akita';
import { Address360, IEntity } from '.';
import { IVendorSalesRep } from './vendorSalesRep.model';
import { IAttachment } from './attachment.model';
import { Column, Entity } from '../entity';
import { ColumnType, LanguagePage, PageTitles } from '../interfaces/types';
import { getBrowserLang } from '@ngneat/transloco';
import moment from 'moment';

/**
 * Object interface for a drop permission on address 360
 */
export interface IBaseDropPermission {
  dropPermissionName: string;
  status: string;
  sourceSystem: string;
  recordType: string;
  createdAt: Date;
}

/**
 * Object interface for a drop permission
 */
export interface IDropPermission extends IEntity {
  id: string;
  dropPermissionName: string;
  serviceAddress?: string;
  createdBy: string;
  modifiedBy: string;
  dateFormatted?: string;
  sourceSystem?: string;
  recordType?: string;
  author?: any;
  status?: any;
  addressId?: any;
  createdByLink?: any;
  href?: string;
  serviceAddressLink?: any;
  contactInformation?: IContactInformation;
  businessProfile?: IBusinessProfileDrop;
  serviceActive?: IServiceActiveDrop;
  confirmation?: IConfirmationDrop;
  systemInformation?: ISystemInformationDrop;
  propertyDrawing?: IPropertyDrawing;
  propertyInformation?: IPropertyInformation;
  providerInformation?: IProviderInformation;
  webSubmissions?: IWebSubmissionsDrop;
  incompletePermissionInformation?: IIncompleteDrop;
  extra?: IDropExtraInformation;
}

/**
 * Object interface, contact information object
 * part of a drop permission
 */
export interface IContactInformation extends IEntity {
  status: string;
  occupantFirstname: string;
  occupantLastname: string;
  occupantPhone: string;
  occupantMobilePhone: string;
  occupantEmail: string;
  occupantTitle: string;
  occupantBuildingOwner: string;
  occupantBuildingOwnerPhone: string;
  secondaryContactTitle: string;
  otherProducts: string;
  statusReason: string;
  buildingOwnerEmail: string;
  customerAvailability: string;
  preferredInstallationDate: Date;
  preferredInstallationTime: string;
  sourceSystem: string;
  additionalComments: string;
  recordType: string;
}

/**
 * Object interface, provider information
 * from a drop permission
 */
export interface IProviderInformation extends IEntity {
  offerExpireDate: Date;
  contractExpiryDate: Date;
  currentMobilityProvider: string;
  currentInternetProvider: string;
  currentProviders: string;
  currentInternetSpeed: string;
  signalStrength: string;
  configuration: string;
  pricing: string;
  preferredPhoneType: string;
}

/**
 * Object interface for the business profile of
 * a drop permission
 */
export interface IBusinessProfileDrop extends IEntity {
  businessName: string;
  numberEmployees: string;
  businessPhoneProvider: boolean;
}

/**
 * Object interface for the information of a property
 * in a drop permission
 */
export interface IPropertyInformation extends IEntity {
  dropType: string;
  additionalPropertyDetails: string;
}

/**
 * Object interface for active services related
 * to a drop permission
 */
export interface IServiceActiveDrop extends IEntity {
  alarmSystem: boolean;
  complexServices: boolean;
  internet: boolean;
  mobility: boolean;
  phone: boolean;
  tv: boolean;
  other: boolean;
  otherServiceActive: string;
}

/**
 * Object interface confirmation section of a
 * drop permission
 */
export interface IConfirmationDrop extends IEntity {
  consent: boolean;
  preferredDeliveryMethod: string;
}

/**
 * Object interface for the property drawing from
 * a drop permission
 */
export interface IPropertyDrawing extends IEntity {
  addressId: string;
  property: string;
}

/**
 * Object interface system information
 * related to a drop permission
 */
export interface ISystemInformationDrop extends IEntity {
  systemGeneratedComments: string;
  gitDPIntegrationResult: string;
  gisDPDropTypeIntegration: string;
  sourceSystem: string;
  vendorSalesRep: string;
  vendorGroup: string;
  vendorSalesRepPhone: string;
}

/**
 * Object interface web submission criteria related
 * to a drop permission
 */
export interface IWebSubmissionsDrop extends IEntity {
  ssoIdentityEmail: string;
  ssoIdentityName: string;
  dealerName: string;
  dealerId: string;
  dealerAgentName: string;
}

/**
 * Object interface incomplete information
 * from a drop permission
 */
export interface IIncompleteDrop extends IEntity {
  reconciliationStatus: string;
  lastReconciliationStatusUpdateDate: Date;
  lastReconciliationStatusUpdateBy: string;
  comments: string;
}

/**
 * Object interface for extra inforation part
 * of a drop permission
 */
export interface IDropExtraInformation extends IEntity {
  constructionPhone: string;
  copperDropType: string;
  dropPermissionObtained: string;
  dropPermissionTerritoryId: ID;
  fibreDropType: string;
  firstNationsReservation: boolean;
  isInsideWiringRequired: boolean;
  offerConsent: boolean;
  storecode: string;
}

@Entity({
  name: PageTitles.drop,
  language: LanguagePage.drop,
})
export class DropPermission implements IDropPermission {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  dropPermissionName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 3,
    },
  })
  status: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 6,
    },
  })
  sourceSystem: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 5,
    },
  })
  recordType: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 4,
    },
  })
  createdAt: Date;

  @Column({
    type: ColumnType.link,
    table: {
      defaultPosition: 2,
    },
  })
  serviceAddressLink: any;

  @Column({
    type: ColumnType.link,
    table: {
      defaultPosition: 7,
    },
  })
  author: any;

  id: string;
  href: string;
  contactInformation: IContactInformation;
  businessProfile: IBusinessProfileDrop;
  serviceActive: IServiceActiveDrop;
  confirmation: IConfirmationDrop;
  systemInformation: ISystemInformationDrop;
  propertyDrawing: IPropertyDrawing;
  propertyInformation: IPropertyInformation;
  providerInformation: IProviderInformation;
  webSubmissions: IWebSubmissionsDrop;
  incompletePermissionInformation: IIncompleteDrop;
  extra: IDropExtraInformation;
  createdBy: string;
  createdByLink: any;
  dropPermissionResponse: any;

  serviceAddress: string;
  address: any;

  // contact information
  statusReason: string;
  additionalComments: string;
  occupantFirstname: string;
  occupantLastname: string;
  occupantPhone: string;
  occupantMobilePhone: string;
  occupantEmail: string;
  occupantBuildingOwner: string;
  occupantBuildingOwnerPhone: string;
  occupantTitle: string;
  secondaryContactTitle: string;
  buildingOwnerEmail: string;
  otherProducts: string;
  customerAvailability: string;
  preferredInstallationDate: Date;
  preferredInstallationTime: string;
  preferredInstallationTimeFormatted: string;

  // confirmation
  consent: boolean;
  preferredDeliveryMethod: string;

  // Provider Information
  configuration: string;
  currentInternetProvider: string;
  currentInternetSpeed: string;
  currentProviders: string;
  offerExpireDate: Date;
  offerExpireDateFormatted: string;
  currentMobilityProvider: string;
  contractExpiryDate: Date;
  contractExpiryDateFormatted: string;
  signalStrength: string;
  pricing: string;
  preferredPhoneType: string;

  // property info
  dropType: string;
  additionalPropertyDetails: string;

  // property drawing
  addressId: ID;
  property: string;

  // active services
  alarmSystem: boolean;
  complexServices: boolean;
  internet: boolean;
  mobility: boolean;
  phone: boolean;
  tv: boolean;
  other: boolean;
  otherServiceActive: string;

  // business profile
  businessName: string;
  numberEmployees: string;

  // incomplete drop
  reconciliationStatus: string;
  lastReconciliationStatusUpdateDate: Date;
  lastReconciliationStatusUpdateBy: string;
  comments: string;

  // extra
  isInsideWiringRequired: boolean;
  offerConsent: boolean;
  constructionPhone: string;
  copperDropType: string;
  dropPermissionObtained: string;
  dropPermissionTerritoryId: ID;
  fibreDropType: string;
  firstNationsReservation: boolean;
  storecode: string;

  dateFormatted: string;

  attachments: IAttachment[];
  salesRepInfo: IVendorSalesRep;
  vendorSalesRep: any;
  vendorSalesRepPhone: string;

  modifiedBy: string;
  modifiedAt: Date;

  /**
   * Creates a new drop permission object
   *
   * @param dropPermission expects an object based on IDropPermission interface
   */
  constructor(
    dropPermission: IDropPermission = {
      id: '',
      addressId: '',
      recordType: '',
      status: '',
      sourceSystem: '',
      dropPermissionName: '',
      createdAt: new Date(),
      modifiedAt: new Date(),
      modifiedBy: '',
      createdBy: '',
      href: '',
      contactInformation: {
        id: '',
        status: '',
        occupantFirstname: '',
        occupantLastname: '',
        occupantPhone: '',
        occupantMobilePhone: '',
        occupantEmail: '',
        occupantTitle: '',
        occupantBuildingOwner: '',
        occupantBuildingOwnerPhone: '',
        secondaryContactTitle: '',
        otherProducts: '',
        statusReason: '',
        buildingOwnerEmail: '',
        customerAvailability: '',
        preferredInstallationDate: new Date(),
        preferredInstallationTime: '',
        sourceSystem: '',
        additionalComments: '',
        recordType: '',
      },
      propertyDrawing: {
        id: '',
        addressId: '',
        property: '',
      },
      propertyInformation: {
        id: '',
        dropType: '',
        additionalPropertyDetails: '',
      },
      confirmation: {
        id: '',
        consent: false,
        preferredDeliveryMethod: '',
      },
      providerInformation: {
        id: '',
        offerExpireDate: new Date(),
        contractExpiryDate: new Date(),
        currentMobilityProvider: '',
        currentInternetProvider: '',
        currentProviders: '',
        currentInternetSpeed: '',
        signalStrength: '',
        configuration: '',
        pricing: '',
        preferredPhoneType: '',
      },
      businessProfile: {
        id: '',
        businessName: '',
        numberEmployees: '',
        businessPhoneProvider: false,
      },
      serviceActive: {
        id: '',
        alarmSystem: false,
        complexServices: false,
        internet: false,
        mobility: false,
        phone: false,
        tv: false,
        other: false,
        otherServiceActive: '',
      },
      systemInformation: {
        id: '',
        systemGeneratedComments: '',
        gitDPIntegrationResult: '',
        gisDPDropTypeIntegration: '',
        sourceSystem: '',
        vendorSalesRep: '',
        vendorGroup: '',
        vendorSalesRepPhone: '',
      },
      incompletePermissionInformation: {
        id: '',
        reconciliationStatus: '',
        lastReconciliationStatusUpdateDate: new Date(),
        lastReconciliationStatusUpdateBy: '',
        comments: '',
      },
      extra: {
        id: '',
        constructionPhone: '',
        copperDropType: '',
        dropPermissionObtained: '',
        dropPermissionTerritoryId: '123',
        fibreDropType: '',
        firstNationsReservation: false,
        isInsideWiringRequired: false,
        offerConsent: false,
        storecode: '',
      },
    },
    salesRepInfo?: IVendorSalesRep
  ) {
    this.dropPermissionResponse = dropPermission;

    this.id = dropPermission.id;
    this.dropPermissionName = dropPermission.dropPermissionName;
    this.createdAt = dropPermission.createdAt;
    this.createdBy = dropPermission.createdBy;
    this.modifiedBy = dropPermission.modifiedBy;
    this.modifiedAt = dropPermission.modifiedAt;
    this.href = dropPermission.href;
    this.contactInformation = dropPermission.contactInformation;
    this.businessProfile = dropPermission.businessProfile;
    this.serviceActive = dropPermission.serviceActive;
    this.confirmation = dropPermission.confirmation;
    this.systemInformation = dropPermission.systemInformation;
    this.propertyDrawing = dropPermission.propertyDrawing;
    this.propertyInformation = dropPermission.propertyInformation;
    this.providerInformation = dropPermission.providerInformation;
    this.webSubmissions = dropPermission.webSubmissions;
    this.incompletePermissionInformation =
      dropPermission.incompletePermissionInformation;
    this.extra = dropPermission.extra;

    // multi-level properties

    // contact information

    this.recordType = `${this.contactInformation?.recordType || this.dropPermissionResponse?.recordType} form`;
    this.status = this.contactInformation?.status || this.dropPermissionResponse?.status;
    this.statusReason = this.contactInformation?.statusReason;
    this.additionalComments = this.contactInformation?.additionalComments;
    this.occupantFirstname = this.contactInformation?.occupantFirstname;
    this.occupantLastname = this.contactInformation?.occupantLastname;
    this.occupantPhone = this.contactInformation?.occupantPhone;
    this.occupantMobilePhone = this.contactInformation?.occupantMobilePhone;
    this.occupantEmail = this.contactInformation?.occupantEmail;
    this.occupantBuildingOwner = this.contactInformation?.occupantBuildingOwner;
    this.occupantBuildingOwnerPhone =
      this.contactInformation?.occupantBuildingOwnerPhone;
    this.occupantTitle = this.contactInformation?.occupantTitle;
    this.secondaryContactTitle = this.contactInformation?.secondaryContactTitle;
    this.buildingOwnerEmail = this.contactInformation?.buildingOwnerEmail;
    this.otherProducts = this.contactInformation?.otherProducts;
    this.customerAvailability = this.contactInformation?.customerAvailability;
    this.preferredInstallationDate =
      this.contactInformation?.preferredInstallationDate;
    this.preferredInstallationTime =
      this.contactInformation?.preferredInstallationTime;
    this.preferredInstallationTimeFormatted =
      this.contactInformation?.preferredInstallationTime;

    // property info
    this.dropType = this.propertyInformation?.dropType;
    this.additionalPropertyDetails =
      this.propertyInformation?.additionalPropertyDetails;

    // Property Drawing
    this.addressId = this.propertyDrawing?.addressId || this.dropPermissionResponse?.addressId;
    this.property = /<\/?[a-z][\s\S]*>/i.test(this.propertyDrawing?.property)
      ? '(image is not valid)'
      : this.propertyDrawing?.property;

    this.serviceAddress = this.propertyDrawing?.addressId;
    this.serviceAddressLink = {
      link: `/app/address360/detail/${this.propertyDrawing?.addressId}`,
      text: this.propertyDrawing?.addressId,
    };

    // confirmation
    this.consent = this.confirmation?.consent;
    this.preferredDeliveryMethod = this.confirmation?.preferredDeliveryMethod;

    // provider information
    this.configuration = this.providerInformation?.configuration;
    this.currentInternetProvider =
      this.providerInformation?.currentInternetProvider;
    this.currentInternetSpeed = this.providerInformation?.currentInternetSpeed;
    this.currentProviders = this.providerInformation?.currentProviders;
    this.offerExpireDate = moment(
      this.providerInformation?.offerExpireDate
    ).toDate();
    this.offerExpireDateFormatted = moment(this.offerExpireDate).format();
    this.currentMobilityProvider =
      this.providerInformation?.currentMobilityProvider;
    this.contractExpiryDate = moment(
      this.providerInformation?.contractExpiryDate
    ).toDate();
    this.contractExpiryDateFormatted = moment(this.contractExpiryDate).format();
    this.signalStrength = this.providerInformation?.signalStrength;
    this.pricing = this.providerInformation?.pricing;
    this.preferredPhoneType = this.providerInformation?.preferredPhoneType;

    // service active
    this.alarmSystem = this.serviceActive?.alarmSystem;
    this.complexServices = this.serviceActive?.complexServices;
    this.internet = this.serviceActive?.internet;
    this.mobility = this.serviceActive?.mobility;
    this.phone = this.serviceActive?.phone;
    this.tv = this.serviceActive?.tv;
    this.other = this.serviceActive?.other;
    this.otherServiceActive = this.serviceActive?.otherServiceActive;

    // business profile
    this.businessName = this.businessProfile?.businessName;
    this.numberEmployees = this.businessProfile?.numberEmployees;

    // systemInformation

    this.salesRepInfo = salesRepInfo;
    const vendorLink =
      this.salesRepInfo?.id && this.salesRepInfo?.id !== ' '
        ? `/app/vendor/sales-rep/detail/${this.salesRepInfo?.id}`
        : undefined;

    this.vendorSalesRep = {
      link: vendorLink,
      text: this.salesRepInfo?.name || this.systemInformation?.vendorSalesRep,
    };

    this.sourceSystem = this.systemInformation?.sourceSystem || this.dropPermissionResponse?.sourceSystem;
    this.vendorSalesRepPhone = this.systemInformation?.vendorSalesRepPhone;

    // incomplete drop
    this.reconciliationStatus =
      this.incompletePermissionInformation?.reconciliationStatus;
    this.lastReconciliationStatusUpdateDate =
      this.incompletePermissionInformation?.lastReconciliationStatusUpdateDate;
    this.lastReconciliationStatusUpdateBy =
      this.incompletePermissionInformation?.lastReconciliationStatusUpdateBy;
    this.comments = this.incompletePermissionInformation?.comments;

    // extra
    this.isInsideWiringRequired = this.extra?.isInsideWiringRequired;
    this.offerConsent = this.extra?.offerConsent;
    this.constructionPhone = this.extra?.constructionPhone;
    this.copperDropType = this.extra?.copperDropType;
    this.dropPermissionObtained = this.extra?.dropPermissionObtained;
    this.dropPermissionTerritoryId = this.extra?.dropPermissionTerritoryId;
    this.fibreDropType = this.extra?.fibreDropType;
    this.firstNationsReservation = this.extra?.firstNationsReservation;
    this.storecode = this.extra?.storecode;

    // sets formatted date

    const dropCreatedDate = new Date(this.createdAt);
    const monthFormatter = new Intl.DateTimeFormat(getBrowserLang(), {
      month: 'short',
    });
    const monthName = monthFormatter.format(dropCreatedDate);
    const hour = dropCreatedDate.getHours() % 12;
    const date = [
      `${monthName.charAt(0).toLocaleUpperCase()}${monthName
        .substr(1)
        .toLocaleLowerCase()}`,
      ' ',
      `${dropCreatedDate.getDate()}`.padStart(2, '0'),
      ', ',
      dropCreatedDate.getFullYear(),
      ' ',
      `${hour ? hour : 12}`.padStart(2, '0'),
      ':',
      `${dropCreatedDate.getMinutes()}`.padStart(2, '0'),
      ' ',
      dropCreatedDate.getHours() >= 12 ? 'PM' : 'AM',
    ];
    this.dateFormatted = `${date.join('')}`;

    const authorName =
      this.salesRepInfo?.name === undefined || this.salesRepInfo?.name === ' '
        ? this.createdBy
        : this.salesRepInfo?.name;
    this.createdByLink = {
      link: vendorLink,
      text: `${authorName}, ${this.dateFormatted}`,
    };

    this.author = {
      link: vendorLink,
      text: authorName,
    };
  }

  /**
   * Returns the last reconciliation status update
   * date in a string with ISO format
   */
  get lastReconcilationStatusUpdateDateFormatted(): string {
    return this.incompletePermissionInformation.lastReconciliationStatusUpdateDate.toISOString();
  }

  /**
   * Sets the last reconciliation status update date
   *
   * @param date a date in a string format
   */
  set lastReconcilationStatusUpdateDateFormatted(date: string) {
    this.incompletePermissionInformation.lastReconciliationStatusUpdateDate =
      new Date(date);
  }

  get addressString(): string {
    return this.address?.serviceAddress;
  }

  get addressPostalCode(): string {
    return this.address?.address?.postalCode;
  }
}

/**
 * Small entity to use in Address360
 */
@Entity({
  name: PageTitles.dropTableView,
  language: LanguagePage.drop,
})
export class DropTableViewAddress360 implements IBaseDropPermission {
  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 1,
    },
  })
  dropPermissionName: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 2,
    },
  })
  recordType: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 3,
    },
  })
  status: string;

  @Column({
    type: ColumnType.string,
    table: {
      defaultPosition: 4,
    },
  })
  sourceSystem: string;

  @Column({
    type: ColumnType.date,
    table: {
      defaultPosition: 5,
    },
  })
  createdAt: Date;

  id: ID;

  constructor(
    dropPermission: IDropPermission = {
      id: '',
      addressId: '',
      recordType: '',
      status: '',
      sourceSystem: '',
      dropPermissionName: '',
      createdAt: new Date(),
      modifiedAt: new Date(),
      modifiedBy: '',
      createdBy: '',
      href: '',
      contactInformation: {
        id: '',
        status: '',
        occupantFirstname: '',
        occupantLastname: '',
        occupantPhone: '',
        occupantMobilePhone: '',
        occupantEmail: '',
        occupantTitle: '',
        occupantBuildingOwner: '',
        occupantBuildingOwnerPhone: '',
        secondaryContactTitle: '',
        otherProducts: '',
        statusReason: '',
        buildingOwnerEmail: '',
        customerAvailability: '',
        preferredInstallationDate: new Date(),
        preferredInstallationTime: '',
        sourceSystem: '',
        additionalComments: '',
        recordType: '',
      },
      systemInformation: {
        id: '',
        systemGeneratedComments: '',
        gitDPIntegrationResult: '',
        gisDPDropTypeIntegration: '',
        sourceSystem: '',
        vendorSalesRep: '',
        vendorGroup: '',
        vendorSalesRepPhone: '',
      },
    }
  ) {
    this.id = dropPermission.id;
    this.dropPermissionName = dropPermission.dropPermissionName;
    this.sourceSystem = dropPermission.systemInformation.sourceSystem;
    this.createdAt = dropPermission.createdAt;
    this.recordType = `${dropPermission.contactInformation.recordType} form`;
    this.status = dropPermission.contactInformation.status;
  }
}
